<template>
  <div class="min-h-screen bg-gray-50 px-4 py-6 pb-28" dir="rtl">
    <div class="max-w-2xl mx-auto space-y-6">
      
      <!-- Header Card -->
      <div class="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-6 text-white">
        <div class="flex items-center gap-3">
          <div class="w-14 h-12 rounded-full bg-white bg-opacity-20 backdrop-blur-sm flex items-center justify-center">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div class="w-full">
            <h2 class="text-xl font-bold">{{ request_info.RequestName }}</h2>
            <p class="text-sm text-teal-50">{{ request_info.RequestCode }}</p>
          </div>
        </div>
      </div>

      <!-- Request Info Card -->
      <div class="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        
        <!-- Last Status -->
        <div class="flex items-center justify-between p-4 bg-teal-50 rounded-lg border border-teal-200">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <svg class="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <label class="text-xs text-gray-600 block">{{ $t('lastStatus') }}</label>
              <span class="text-sm font-bold text-gray-900">{{ request_info.LastStatus }}</span>
            </div>
          </div>
        </div>

        <!-- Request Details -->
        <div class="space-y-3">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('requestNumber') }}</label>
              <p class="text-sm font-bold text-gray-900">{{ request_info.RequestCode }}</p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('registerDate') }}</label>
              <p class="text-sm font-medium text-gray-900">{{ request_info.RequestTime }}</p>
            </div>
          </div>

          <div v-if="request_info.Summary" class="flex items-start gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('description') }}</label>
              <p class="text-sm text-gray-900">{{ request_info.Summary }}</p>
            </div>
          </div>

          <!-- Mobile Numbers -->
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('mobileNo') }}</label>
              <div class="flex items-center justify-between">
                <a :href="'tel:'+request_info.CustomerMobile" class="text-sm font-medium text-teal-600">
                  {{ request_info.CustomerMobile }}
                </a>
                <a :href="'tel:'+request_info.CustomerMobile" class="w-8 h-8 rounded-lg bg-teal-100 hover:bg-teal-200 flex items-center justify-center transition-colors">
                  <svg class="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </a>
              </div>
            </div>
            </div>

          <div v-if="request_info.OtherPhoneNO != null" class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('secoundMobileNo') }}</label>
              <div class="flex items-center justify-between">
                <a :href="'tel:'+request_info.OtherPhoneCode+request_info.OtherPhoneNO" class="text-sm font-medium text-teal-600">
                  {{ request_info.OtherPhoneCode }}{{ request_info.OtherPhoneNO }}
                </a>
                <a :href="'tel:'+request_info.OtherPhoneCode+request_info.OtherPhoneNO" class="w-8 h-8 rounded-lg bg-teal-100 hover:bg-teal-200 flex items-center justify-center transition-colors">
                  <svg class="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div v-if="request_info.StatusSummary" class="flex items-start gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('referDescription') }}</label>
              <p class="text-sm text-gray-900">{{ request_info.StatusSummary }}</p>
            </div>
          </div>

          <!-- Execution Statuses -->
          <div v-if="request_info.EjraiStatusSummary!=null" class="space-y-2">
            <label class="text-sm font-bold text-gray-900">وضعیت های اجرائی</label>
            <div 
              v-for="ejraii in JSON.parse(request_info.EjraiStatusSummary)" 
              :key="ejraii.StatusName"
              class="p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <p class="text-sm font-medium text-gray-900">{{ ejraii.StatusName }}</p>
              <p class="text-xs text-gray-600 mt-1">{{ ejraii.Summary }}</p>
            </div>
                      </div>
                    </div>

      </div>
      <!-- Addresses Section -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-lg font-bold text-gray-900 mb-4">{{ $t('addresses') }}</h3>
        <div class="space-y-4">
          <div 
            v-for="(item_address, index) in request_info.Addresses"
            :key="index"
            class="p-4 bg-gray-50 border border-gray-200 rounded-lg"
          >
            <div class="flex items-start gap-3 mb-3">
              <div class="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                <svg class="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div class="w-full">
                <p class="text-sm text-gray-900 font-medium">{{ get_request_address(item_address) }}</p>
                <div class="flex items-center justify-between mt-2">
                  <a :href="'tel:'+item_address.PhoneCode+item_address.PhoneNO" class="text-sm font-medium text-teal-600">
                    {{ item_address.PhoneCode }}{{ item_address.PhoneNO }}
                  </a>
                  <a :href="'tel:'+item_address.PhoneCode+item_address.PhoneNO" class="w-8 h-8 rounded-lg bg-teal-100 hover:bg-teal-200 flex items-center justify-center transition-colors">
                    <svg class="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </a>
                  </div>

                <div v-if="item_address.FromTime !=null" class="mt-2 flex items-center gap-2 text-xs text-gray-500">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{{ item_address.FromTime }} - {{ item_address.ToTime }}</span>
                </div>

                <button 
                  v-if="item_address.Lat !=null"
                  @click="go_direction(item_address)"
                  class="mt-3 w-full px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  {{ $t('navigation') }}
                </button>
              </div>
            </div>
          </div>
        </div>
            </div>

      <!-- Action Button -->
      <button 
        v-if="action_button_text != ''"
        @click="go_to_categories()"
        class="w-full bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white py-4 px-6 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2"
      >
        {{ action_button_text }}
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
          </button>

    </div>

    <!-- Service Location Dialog -->
    <div v-if="dialog_service_out_branch" class="fixed inset-0 bg-black bg-opacity-50 z-999 flex items-center justify-center p-4">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <!-- Header -->
        <div class="px-6 py-4 bg-gradient-to-r from-teal-600 to-teal-700">
          <h2 class="text-lg font-bold text-white text-center">{{ $t('requestStatus') }}</h2>
        </div>
        
        <!-- Body -->
        <div class="px-6 py-6">
          <p class="text-sm text-gray-700 text-center">{{ $t('isRequestInBranchOrNot') }}</p>
        </div>
        
        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button 
            class="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200"
            @click="service_out_branch_action(8)"
          >
            {{ $t('inLocation') }}
          </button>
          <button 
            class="flex-1 px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-medium rounded-lg transition-all duration-200"
            @click="service_out_branch_action(7)"
          >
            {{ $t('inBranch') }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>
<script>
  import { bus } from '../../main.js'

  export default {
    props: [
      'passing_data'
    ],
    data () {
      return {
        errors: [],
        request_info: [],
        request_statuses: [],
        action_button_text: '',
        next_action_status: 0,
        dialog_service_out_branch: false,
        technician_name: 'hamid mokhtari',
        device_password: 'hamid mokhtari',


      }
    },
watch: {
      selected_province: function () {
        this.get_cities()
      }
    },
mounted () {
      // var passing_data = JSON.parse(localStorage.getItem('request_data'));
      // this.enable_send_from_country = passing_data.on_site_service;
      // this.get_phone_prefix()
      this.get_request_info()
      // this.get_provinces()
    },
    methods: {
      get_request_address (item) {
        var address = ''
        if (item.CityName != null) {
          address += item.CityName + ' ,'
        }
        if (item.AreaName != null) {
          address += item.AreaName + ' ,'
        }
        if (item.AddressText != null) {
          address += item.AddressText + ' '
        }

        return address
      },
      go_direction (item) {
        var url = 'geo:0,0?q=' + item.Lat + ',' + item.Lng
        window.open(url, '_blank')
      },
      get_request_info () {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          UserId: user_id,
          ReqId: this.$route.params.rec_id,
          IsFinalize: 0,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/UserReqDash', send_data, {})
          .then(response => {
            if (response.data.error == 0) {
              self.request_info = response.data.recordInfo
              self.get_device_status()
              self.$route.meta.name = self.$t('showPreRequest') + self.request_info.RequestCode
            }
            // this.$router.push({ path: "/post-login", params: {} });
          }).catch(e => {
          this.errors.push(e)
        })
      },
      get_request_full_info () {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          UserId: user_id,
          ReqId: this.$route.params.rec_id,
          IsFinalize: 0,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/GetPreReqRecInfo', send_data, {})
          .then(response => {
            if (response.data.error == 0) {
              // self.request_info = response.data.recordInfo;
              // self.get_device_status();
              var PassingData = { on_site_service: true }
              PassingData.ServiceOutBranch = response.data.recordInfo.ServiceOutBranch
              PassingData.ServiceOutBranchOnFinalize = '0'
              PassingData.PreRequestId = response.data.recordInfo.Id
              PassingData.on_site_service = true
              PassingData.cat_id = response.data.recordInfo.ProductTypeId
              PassingData.group_id = response.data.recordInfo.CategoryId
              PassingData.sub_group_id = response.data.recordInfo.GroupId
              PassingData.brand_id = response.data.recordInfo.SubGroupId
              PassingData.customer_summary = response.data.recordInfo.Description
              PassingData.model_id = response.data.recordInfo.ModelId
              PassingData.model_title = response.data.recordInfo.ModelTitle
              PassingData.other_phone_code = response.data.recordInfo.OtherPhoneCode
              PassingData.other_phone_no = response.data.recordInfo.OtherPhoneNO
              PassingData.customer_mobile = response.data.recordInfo.PhoneNO
              PassingData.customer_name = ''
              PassingData.stateId = 'final_pre_request'
              // data.put("typeId", String.valueOf(model.getRec().getProductTypeId()));
              // data.put("parentTitle", "درخواست");
              // data.put("stateId", "editrequest");
              // data.put("customer_name", "");
              // data.put("delivery_status", "true");
              PassingData.request_name = self.$t('repair')

              localStorage.setItem('request_data', JSON.stringify(PassingData))
              console.log(PassingData)
              if (PassingData.ServiceOutBranch == true) {
                self.dialog_service_out_branch = true
              } else {
                self.$router.push({ name: 'groups', params: {} })
              }
            }
            // this.$router.push({ path: "/post-login", params: {} });
          }).catch(e => {
          this.errors.push(e)
        })
      },
      service_out_branch_action (type) {
// console.log(type,this.request_statuses);
        var data = JSON.parse(localStorage.getItem('request_data'))
        data.ServiceOutBranchOnFinalize = this.request_statuses[type]
        localStorage.setItem('request_data', JSON.stringify(data))

        this.$router.push({ name: 'groups', params: {} })
      },
      go_to_categories () {
        var has_exist = false
        var self = this
        // console.log("statuses is "+JSON.stringify(this.request_statuses));
        var has_exist = false
        self.request_statuses.forEach(function (number) {
          if (self.request_info.StatusId == number) {
            has_exist = true
          }
        })
        if (!has_exist) {
          self.action_button_text = 'شروع به حرکت'
          self.next_action_status = self.request_statuses[0]
        }

        if (self.request_info.StatusId == self.request_statuses[0]) {
          self.action_button_text = self.$t('submit_receive')
          self.next_action_status = self.request_statuses[1]
        }

        if (self.request_info.StatusId == self.request_statuses[1]) {
          self.action_button_text = self.$t('finalize_request')
          self.next_action_status = self.request_statuses[1]
          self.get_request_full_info()
          return false
        }

        this.change_request_status(this.next_action_status)
      },
      get_device_status () {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        console.log()
        const send_data = {
          UserId: user_id,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/GetStatusSetting', send_data, {})
          .then(response => {
            self.request_statuses = response.data.rslt
            var has_exist = false
            self.request_statuses.forEach(function (number) {
              if (self.request_info.StatusId == number) {
                has_exist = true
              }
            })
            if (!has_exist) {
              self.action_button_text = self.$t('start_driving')
              self.next_action_status = self.request_statuses[0]
            } else if (self.request_info.StatusId == self.request_statuses[0]) {
              self.action_button_text = this.$t('submit_receive')
              self.next_action_status = self.request_statuses[1]
            } else if (self.request_info.StatusId == self.request_statuses[1]) {
              self.action_button_text = this.$t('finalize_request')
              self.next_action_status = self.request_statuses[1]
            } else {
              self.action_button_text = ''
              self.next_action_status = 0
            }
          }).catch(e => {
          this.errors.push(e)
        })
      },
      change_request_status (status_id) {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        console.log()
        const send_data = {
          UserId: user_id,
          ReqId: this.$route.params.rec_id,
          Desc: '',
          StatusId: status_id,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/ChangePreRequestStatus', send_data, {})
          .then(response => {
            self.get_request_info()
          }).catch(e => {
          this.errors.push(e)
        })
      },

    }
  }
</script>
